import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { Link } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import isMock from "../../config";
import {useMaterialUIController} from "../../context";
import {Box} from "@mui/material";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

// ================== FETCH FUNCTION ==================


function Organizations() {

    const [globalFilter, setGlobalFilter] = useState('');
    const [usersPagination, setUsersPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [usersFilter, setUsersFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState(0);
    const [allOrgs, setAllOrgs] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState("");
    const [newUserName, setNewUserName] = useState("");
    const [oIdToInvite, setOIdToInvite] = useState("");
    const [emailError, setEmailError] = useState("");
    const [usersRows, setUsersRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [parentSearch, setParentSearch] = useState("");
    const [parentOptions, setParentOptions] = useState([]);
    const [parentLoading, setParentLoading] = useState(false);
    const [parentObj, setParentObj] = useState(null); // { id, name } | null
    const [isAddOrgDialogOpen, setIsAddOrgDialogOpen] = useState(false);
    const [orgName, setOrgName] = useState("");
    const [orgPhone, setOrgPhone] = useState("");
    const [orgWebsite, setOrgWebsite] = useState("");
    const [orgAddress, setOrgAddress] = useState("");
    const [orgParentId, setOrgParentId] = useState("");
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [newUserFirstName, setNewUserFirstName] = useState("");
    const [newUserLastName, setNewUserLastName] = useState("");

    const [orgToDelete, setOrgToDelete] = useState(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isDeleteFinalConfirmOpen, setIsDeleteFinalConfirmOpen] = useState(false);

    const [controller] = useMaterialUIController();
    useEffect(() => {
        setUsersPagination((p) => ({ ...p, pageIndex: 0 }));
    }, [usersFilter]);


    useEffect(() => {
        let cancelled = false;

        async function run() {
            const q = (parentSearch || "").trim();
            // if empty, maybe show first 20 roots or recent; but keep it simple:
            setParentLoading(true);
            try {
                const url =
                    `https://blue-parking-invite.bluept.dev/api/pal-organizations/search` +
                    `?limit=20&skip=0&filter=${encodeURIComponent(q)}`;

                const res = await fetch(url);
                if (!res.ok) throw new Error("Failed to search organizations");
                const data = await res.json();

                if (!cancelled) {
                    setParentOptions(data.organizations || []);
                }
            } catch (e) {
                if (!cancelled) setParentOptions([]);
                console.error(e);
            } finally {
                if (!cancelled) setParentLoading(false);
            }
        }

        run();
        return () => { cancelled = true; };
    }, [parentSearch]);


    const fetchUsers = async ({ queryKey }) => {
        const [, pageIndex, pageSize, filter] = queryKey;
        const skip = pageIndex * pageSize;

        const url =
            `https://blue-parking-invite.bluept.dev/api/pal-web-users` +
            `?limit=${pageSize}&skip=${skip}&filter=${encodeURIComponent(filter || "")}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch pal web users");
        return res.json();
    };

    const { data: usersResp, isLoading: isUsersLoading } = useQuery({
        queryKey: ["palWebUsers", usersPagination.pageIndex, usersPagination.pageSize, usersFilter],
        queryFn: fetchUsers,
        keepPreviousData: true,
    });

    const users = usersResp?.users || [];
    const usersTotal = Number(usersResp?.total || 0);

// when searching -> reset to page 0
    useEffect(() => {
        setUsersPagination((p) => ({ ...p, pageIndex: 0 }));
    }, [usersFilter]);

    const { darkMode } = controller;
    const textColor = darkMode ? "#fff" : "#000";
    const cardBgColor = darkMode ? "#1e293b" : "#fff";
    function findNestedSubOrg(org, targetId) {
        if (!org.subOrganizations) return null;

        for (const sub of org.subOrganizations) {
            if (sub.id === targetId) {
                return sub;
            }
            // If not found at this level, recurse deeper
            const found = findNestedSubOrg(sub, targetId);
            if (found) return found;
        }

        return null;
    }

    async function findTopLevelOrgId(orgId) {
        let currentId = orgId;
        let topLevelId = orgId;

        while (currentId) {
            const res = await fetch(`http://localhost:3001/organizations/${currentId}`);
            if (!res.ok) throw new Error(`Org not found: ${currentId}`);
            const orgData = await res.json();

            if (!orgData.parentOrganizationId) {
                // Found the top-level org
                topLevelId = orgData.id;
                break;
            } else {
                // Climb up one level
                currentId = orgData.parentOrganizationId;
            }
        }

        return topLevelId;
    }

    async function addSubOrgToTopLevelParent(parentOrgId, newOrgData) {
        // 1) Find top-level org ID
        const topLevelId = await findTopLevelOrgId(parentOrgId);

        // 2) Fetch the top-level org data
        const topRes = await fetch(`http://localhost:3001/organizations/${topLevelId}`);
        if (!topRes.ok) throw new Error(`Failed to fetch top-level org ${topLevelId}`);
        const topLevelOrg = await topRes.json();

        // 3) Recursively locate 'parentOrgId' within 'topLevelOrg'
        const parentOrgInTree = findNestedSubOrg(topLevelOrg, parentOrgId);
        if (parentOrgInTree) {


            // 4) Add newOrgData to parentOrgInTree.subOrganizations
            if (Array.isArray(parentOrgInTree.subOrganizations)) {
                parentOrgInTree.subOrganizations = [...parentOrgInTree.subOrganizations, newOrgData];
            } else {
                parentOrgInTree.subOrganizations = [newOrgData];
            }
        }
        else {
            // If the parentOrgId is not found in the top-level org's subOrganizations, it's possible that the parentOrgId is the top-level org itself
            // In this case, we should add the newOrgData to the top-level org's subOrganizations
            if (Array.isArray(topLevelOrg.subOrganizations)) {
                topLevelOrg.subOrganizations = [...topLevelOrg.subOrganizations, newOrgData];
            } else {
                topLevelOrg.subOrganizations = [newOrgData];
            }
        }

        // 5) PUT the entire updated top-level org back to JSON Server
        const putRes = await fetch(`http://localhost:3001/organizations/${topLevelId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(topLevelOrg),
        });
        if (!putRes.ok) {
            throw new Error(`Failed to update top-level org ${topLevelId}`);
        }
    }
    const fetchOrganizations = async ({ queryKey }) => {
        const [, pageIndex, pageSize, filter] = queryKey;
        const skip = pageIndex * pageSize;

        const url =
            `https://blue-parking-invite.bluept.dev/api/pal-organizations/tree` +
            `?limit=${pageSize}&skip=${skip}&filter=${encodeURIComponent(filter || "")}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch organizations");
        const data = await res.json();

        setRowCount(Number(data.totalRoots || 0));
        setAllOrgs(data.organizations || []); // if you still need dropdown

        return data.organizations || [];
    };

    const {
        data: organizations = [],
        isLoading,
        error,
        refetch: refetchOrgs, // ✅ add this
    } = useQuery({
        queryKey: ["palOrgsTree", pagination.pageIndex, pagination.pageSize, globalFilter],
        queryFn: fetchOrganizations,
        keepPreviousData: true,
    });
    const theme = createTheme({
        components: {
            MuiTablePagination: {
                styleOverrides: {
                    root: {
                        backgroundColor: "#f5f5f5",
                        color: "#000",
                    },
                    selectIcon: {
                        color: "#000",
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: "#000",
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: "#000",
                    },
                },
            },
        },
    });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleInvite = (oId) => {
        setIsDialogOpen(true);
        setOIdToInvite(oId);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setNewUserEmail("");
        setNewUserName("");
        setEmailError("");
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddUser = async () => {
        if (!validateEmail(newUserEmail)) {
            setEmailError("Please enter a valid email address.");
            return;
        }

        try {
            const res = await fetch(`https://blue-parking-invite.bluept.dev/api/pal-organizations/${oIdToInvite}/users`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: newUserEmail,
                    firstname: newUserFirstName,
                    lastname: newUserLastName,
                }),
            });

            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to add user");

            handleDialogClose();
            // if you want: refetch users table
            // usersRefetch();
        } catch (e) {
            console.error(e);
            setEmailError(e.message || "Failed to add user");
        }
    };


    // Adding a new organization
    const handleOpenAddOrgDialog = () => {
        setIsAddOrgDialogOpen(true);
    };

    const handleCloseAddOrgDialog = () => {
        setIsAddOrgDialogOpen(false);
        // Clear form
        setOrgName("");
        setOrgPhone("");
        setOrgWebsite("");
        setOrgAddress("");
        setOrgParentId("");
        setParentSearch("");
        setParentOptions([]);
        setParentObj(null);
    };

    const handleAddOrg = async () => {
        try {
            const res = await fetch("https://blue-parking-invite.bluept.dev/api/pal-organizations", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: orgName,
                    address_name: orgAddress,
                    parent_id: orgParentId || null,
                }),
            });

            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to create org");

            // ✅ ensure table updates before closing (optional but nice)
            await refetchOrgs();

            // ✅ close for sure
            handleCloseAddOrgDialog();
        } catch (err) {
            console.error("Error adding organization:", err);
            // optional: show error in UI
        }
    };

    const openFirstDeleteDialog = (org) => {
        setOrgToDelete(org);
        setIsDeleteFinalConfirmOpen(true);
    };

    // const closeFirstDeleteDialog = () => {
    //     setIsDeleteConfirmOpen(false);
    //     setOrgToDelete(null);
    // };
    //
    // const confirmFirstDelete = () => {
    //     // Close the first, open the second
    //     setIsDeleteConfirmOpen(false);
    //     setIsDeleteFinalConfirmOpen(true);
    // };

    const closeFinalDeleteDialog = () => {
        setIsDeleteFinalConfirmOpen(false);
        setOrgToDelete(null);
    };

    function removeChildOrgById(org, targetId) {
        if (!org.subOrganizations) {
            return false;
        }

        // Look for a direct child match
        const index = org.subOrganizations.findIndex((sub) => sub.id === targetId);
        if (index !== -1) {
            // Remove this child from the array
            org.subOrganizations.splice(index, 1);
            return true;
        }

        // Not found at this level—recurse deeper
        for (const sub of org.subOrganizations) {
            const removed = removeChildOrgById(sub, targetId);
            if (removed) return true;
        }

        // Not found anywhere
        return false;
    }

    async function removeSubOrgFromTopLevelParent(orgId) {
        // 1) Identify which top-level org actually contains the org to delete
        const topLevelId = await findTopLevelOrgId(orgId); // same logic you used before

        // 2) Fetch the top-level org object
        const topRes = await fetch(`http://localhost:3001/organizations/${topLevelId}`);
        if (!topRes.ok) throw new Error(`Failed to fetch top-level org ${topLevelId}`);
        const topLevelOrg = await topRes.json();

        // 3) Recursively remove the orgId from topLevelOrg's nested subOrganizations
        const didRemove = removeChildOrgById(topLevelOrg, orgId);

        if (!didRemove) {
            console.warn(`Could not find org ${orgId} inside top-level org ${topLevelId}'s subOrganizations`);
            // It's possible it doesn't exist there, or it's a top-level org with no parent
            return;
        }

        // 4) PUT the updated top-level org back
        const putRes = await fetch(`http://localhost:3001/organizations/${topLevelId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(topLevelOrg),
        });
        if (!putRes.ok) {
            throw new Error(`Failed to update top-level org ${topLevelId}`);
        }
    }

    const handleFinalDelete = async () => {
        if (!orgToDelete) return;

        try {
            const res = await fetch(`https://blue-parking-invite.bluept.dev/api/pal-organizations/${orgToDelete.id}`, {
                method: "DELETE",
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.error || "Delete failed");

            await refetchOrgs();
        } catch (e) {
            console.error("Error deleting organization:", e);
        }

        setIsDeleteFinalConfirmOpen(false);
        setOrgToDelete(null);
    };


    const filteredOrganizationRows = (organizations || [])
        .map((org, index) => ({
            ...org,
            organizationId: (index + 1).toString(),
            address: org.address ?? org.address_name ?? "", // normalize
        }))
        .filter((row) => {
            const name = (row.name || "").toLowerCase();
            const address = (row.address || "").toLowerCase();
            const id = String(row.organizationId || "");
            const q = (searchTerm || "").toLowerCase();

            return name.includes(q) || address.includes(q) || id.includes(q);
        });

    // Columns
    const organizationColumns = [

        {
            accessorKey: "id",
            header: "Organization ID",
            enableSorting: false,
        },
        {
            accessorKey: "name",
            header: "Organization Name",
            Cell: ({ row }) => {
                const depth = row.depth || 0;

                return (
                    <Box sx={{ display: "flex", alignItems: "center", pl: `${depth * 18}px` }}>
                        {depth > 0 && (
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    mr: 1,
                                    opacity: 0.8,
                                    backgroundColor: darkMode ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
                                }}
                            />
                        )}

                        <Link
                            to={`/organizations/${row.original.id}`}
                            style={{
                                color: darkMode ? "#7DD3FC" : "#0B63CE",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: depth === 0 ? 700 : 500,
                            }}
                        >
                            {row.original.name}
                        </Link>
                    </Box>
                );
            },
        },

        {
            accessorKey: "address",
            header: "Address",
        },
        {
            accessorKey: "actions",
            header: "Actions",
            enableSorting: false,
            // Two actions side by side: Invite Users, Delete
            Cell: ({ row }) => (
                <MDBox display="flex" gap={1}>
                    <Button
                        variant="contained"
                        color="primary"
                        // startIcon={}
                        onClick={() => handleInvite(row.original.id)}
                    >
                        <Icon>group_add</Icon>
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        // startIcon={}
                        onClick={() => openFirstDeleteDialog(row.original)}
                    >
                        <Icon>delete</Icon>
                    </Button>
                </MDBox>
            ),
        },
    ];

    if (isLoading) return <MDTypography>Loading...</MDTypography>;
    if (error) return <MDTypography>Error: {error.message}</MDTypography>;
    const columns = [
        {
            accessorKey: "name",
            header: "Name",
            Cell: ({ row }) => (
                <a
                    href={`/places/${row.original.id}`}
                    style={{ color: darkMode ? "#059be5" : "blue", textDecoration: "underline", cursor: "pointer" }}
                >
                    {row.original.name}
                </a>
            ),
            filterFn: (row, columnId, filterValue) => searchRowRecursively(row, columnId, filterValue),
        },
        {
            accessorKey: "address",
            header: "Address",
            filterFn: (row, columnId, filterValue) => searchRowRecursively(row, columnId, filterValue),
        },
        // NEW ACTION COLUMN FOR DELETION
        {
            id: "delete",
            header: "Delete",
            Cell: ({ row }) => (
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleOpenDeleteDialog(row.original.id)}
                >
                    Delete
                </Button>
            ),
        },
    ];
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Tabs
                    sx={{ marginBottom: 5 }}
                    value={activeTab}
                    onChange={handleTabChange}
                    centered
                >
                    <Tab label="Organization" />
                    <Tab label="Organization Users" />
                </Tabs>

                {activeTab === 0 && (
                    <MDBox mb={3} style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button variant="contained" color="secondary" onClick={handleOpenAddOrgDialog}>
                            <Icon>add</Icon>
                        </Button>
                    </MDBox>
                )}

                {activeTab === 0 && (
                    <ThemeProvider theme={theme}>
                        <MaterialReactTable
                            columns={organizationColumns}
                            data={filteredOrganizationRows}
                            enableExpanding
                            manualFiltering
                            manualPagination // ✅ IMPORTANT
                            rowCount={rowCount} // ✅ IMPORTANT
                            state={{
                                isLoading,
                                pagination,
                                globalFilter,
                            }}
                            onPaginationChange={setPagination}
                            onGlobalFilterChange={setGlobalFilter}

                            muiBottomToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: darkMode ? '#fff' : '#1e293b',
                                    ".MuiFormLabel-root": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-icon": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-select": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiBox-root.MuiButtonBase-root" : {
                                            color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                }
                            }}
                            muiTopToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: '#fff',
                                    ".MuiSvgIcon-root" : {

                                        color: darkMode ? '#fff' : '#1e293b',

                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },

                            }}
                            muiTableBodyCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                                    color: darkMode ? "#ffffff" : "#000",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    }
                                },
                            }}
        //                     renderDetailPanel={({ row }) => (
        //                         <div style={{ padding: "16px", backgroundColor: cardBgColor}}>
        //                             <div style={{ display: "flex", alignItems: "center" , marginBottom: "20px" }}>
        //                                 <Icon sx={{ color:textColor}}>location_on</Icon>  <span style={{ color:textColor, fontWeight: "bold"}}>Places:</span>
        //
        //                                 {/*
        //   // Alternatively, if using a specific icon:
        //   <LocationOnIcon style={{ marginLeft: "4px" }} />
        // */}
        //                             </div>
        //                             <MaterialReactTable
        //                                 columns={columns}
        //                                 data={row.original.places || []}
        //                                 // enableExpanding
        //                                 getSubRows={(row) => row?.subPlaces}
        //                                 // enableGlobalFilter
        //                                 enableTopToolbar={false} // Hide the top toolbar
        //                                 enableGlobalFilter={false} // Disable global search
        //                                 enableColumnFilters={false} // Disable per-column filters
        //                                 enableSorting={false} // Disable sorting, if you want
        //                                 muiBottomToolbarProps={{
        //                                     sx: {
        //                                         backgroundColor: darkMode ? '#1e293b' : '#fff',
        //                                         color: darkMode ? '#fff' : '#1e293b',
        //                                         ".MuiFormLabel-root": {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         },
        //                                         ".MuiSelect-icon": {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         },
        //                                         ".MuiSelect-select": {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         },
        //                                         ".MuiBox-root.MuiButtonBase-root" : {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         },
        //                                         ".MuiSvgIcon-root" : {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         },
        //                                         '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
        //                                             // pick a dimmer color for disabled icons
        //                                             color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        //                                         },
        //                                     }
        //                                 }}
        //                                 muiTopToolbarProps={{
        //                                     sx: {
        //                                         backgroundColor: darkMode ? '#1e293b' : '#fff',
        //                                         color: '#fff',
        //                                         ".MuiSvgIcon-root" : {
        //
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //
        //                                         },
        //                                         ".MuiTableSortLabel-iconDirectionAsc": {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         },
        //                                         '& .MuiInputBase-input': {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         }
        //                                     },
        //
        //                                 }}
        //                                 muiTableBodyCellProps={{
        //                                     sx: {
        //                                         backgroundColor: darkMode ? "#1e293b" : "#ffffff",
        //                                         color: darkMode ? "#ffffff" : "#000",
        //                                         ".MuiSvgIcon-root" : {
        //                                             color: darkMode ? "#ffffff" : "#000",
        //                                         }
        //                                     },
        //                                 }}
        //                                 muiTableHeadCellProps={{
        //                                     sx: {
        //                                         backgroundColor: darkMode ? "#1e293b" : "#0098CF",
        //                                         color: "white",
        //                                         fontWeight: "bold",
        //                                         ".MuiSvgIcon-root" : {
        //                                             color: darkMode ? "#ffffff" : "#000",
        //                                         },
        //                                         // then specifically target the sort label icon:
        //                                         '& .MuiTableSortLabel-icon': {
        //                                             color: darkMode ? '#fff !important' : '#000 !important',
        //                                         },
        //                                         // likewise for iconDirectionAsc / iconDirectionDesc if needed
        //                                         '& .MuiTableSortLabel-iconDirectionAsc': {
        //                                             color: darkMode ? '#fff !important' : '#000 !important',
        //                                         },
        //                                         '& .MuiTableSortLabel-iconDirectionDesc': {
        //                                             color: darkMode ? '#fff !important' : '#000 !important',
        //                                         },
        //                                         '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
        //                                             // pick a dimmer color for disabled icons
        //                                             color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        //                                         },
        //                                         '& .MuiInputBase-input': {
        //                                             color: darkMode ? '#fff' : '#1e293b',
        //                                         }
        //                                     },
        //                                 }}
        //                                 globalFilterFn="recursiveSearch"
        //
        //                                 filterFns={{
        //                                     recursiveSearch: (row, columnId, filterValue) => {
        //                                         return searchRowRecursively(row, columnId, filterValue);
        //                                     },
        //                                 }}
        //                                 // Style the top toolbar (the area containing search, column toggles, etc.)
        //                                 muiTableHeadProps={{
        //                                     sx: {
        //                                         display: "table-header-group",
        //                                         backgroundColor: "rgb(0,0,0)",
        //                                         color: "white",
        //                                     },
        //                                 }}
        //                                 initialState={{
        //                                     expanded: false,
        //                                 }}
        //                                 muiTableContainerProps={{
        //                                     sx: {
        //                                         width: "100%",
        //                                         overflowX: "auto",
        //                                         backgroundColor: "#15aff4",
        //                                         color: "white",
        //                                     },
        //                                 }}
        //
        //                             />
        //
        //                         </div>
        //                     )}

                            muiTableHeadCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                                    color: "white",
                                    fontWeight: "bold",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    },
                                    // then specifically target the sort label icon:
                                    '&. MuiTableRow-root': {
                                        backgroundColor: cardBgColor
                                    },
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    // likewise for iconDirectionAsc / iconDirectionDesc if needed
                                    '& .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },
                            }}
                            muiTableContainerProps={{
                                sx: {
                                    width: "100%",
                                    overflowX: "auto",
                                    backgroundColor: cardBgColor,
                                },
                            }}
                            getSubRows={(row) => row?.subOrganizations}
                        />
                    </ThemeProvider>
                )}

                {activeTab === 1 && (
                    <ThemeProvider theme={theme}>
                        <MaterialReactTable
                            columns={[
                                { accessorKey: "id", header: "Email" },
                                { accessorKey: "firstname", header: "First Name" },
                                { accessorKey: "lastname", header: "Last Name" },
                                { accessorKey: "status", header: "Status" },

                                // ✅ show orgs_json nicely
                                {
                                    id: "orgs",
                                    header: "Organizations",
                                    accessorFn: (row) =>
                                        (row?.orgs_json || []).map((o) => o?.name).filter(Boolean).join(", "),
                                    Cell: ({ row }) => {
                                        const orgs = row.original.orgs_json || [];
                                        if (!orgs.length) return "-";

                                        return (
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                {orgs.slice(0, 8).map((o) => (
                                                    <Chip
                                                        key={o._id || o.id || o.name}
                                                        label={o.name || o._id}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            color: darkMode ? "#fff" : "#000",
                                                            borderColor: darkMode ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.25)",
                                                        }}
                                                    />
                                                ))}
                                                {orgs.length > 8 && (
                                                    <Chip
                                                        label={`+${orgs.length - 8}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            color: darkMode ? "#fff" : "#000",
                                                            borderColor: darkMode ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.25)",
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        );
                                    },
                                },
                            ]}
                            data={users}
                            muiBottomToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: darkMode ? '#fff' : '#1e293b',
                                    ".MuiFormLabel-root": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-icon": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-select": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiBox-root.MuiButtonBase-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                }
                            }}
                            muiTopToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: '#fff',
                                    ".MuiSvgIcon-root" : {

                                        color: darkMode ? '#fff' : '#1e293b',

                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },

                            }}
                            muiTableBodyCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                                    color: darkMode ? "#ffffff" : "#000",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    }
                                },
                            }}
                            muiTableHeadCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                                    color: "white",
                                    fontWeight: "bold",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    },
                                    // then specifically target the sort label icon:
                                    '&. MuiTableRow-root': {
                                        backgroundColor: cardBgColor
                                    },
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    // likewise for iconDirectionAsc / iconDirectionDesc if needed
                                    '& .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },
                            }}
                            manualPagination
                            manualFiltering
                            rowCount={usersTotal}
                            onPaginationChange={setUsersPagination}
                            onGlobalFilterChange={setUsersFilter}
                            state={{
                                isLoading: isUsersLoading,
                                pagination: usersPagination,
                                globalFilter: usersFilter,
                            }}
                        />
                    </ThemeProvider>
                )}
            </MDBox>

            {/*
        =========== DIALOG: Invite a User ===========
      */}
            <Dialog open={isDialogOpen} onClose={handleDialogClose}>
                <DialogTitle style={{ color: "#000" }}>Add User to Organization</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="User Email"
                        variant="outlined"
                        value={newUserEmail}
                        onChange={(e) => {
                            setNewUserEmail(e.target.value);
                            setEmailError("");
                        }}
                        sx={{
                            marginTop: "10px",
                            "& .MuiInputLabel-root": {
                                color: "#000",
                            },
                            "& .MuiInputBase-input": {
                                color: "#000",
                            },
                        }}
                        error={!!emailError}
                        helperText={emailError}
                    />
                    <TextField
                        fullWidth
                        label="First Name"
                        value={newUserFirstName}
                        onChange={(e) => setNewUserFirstName(e.target.value)}
                        style={{ marginTop: "16px" }}
                        sx={{
                            marginTop: "10px",
                            "& .MuiInputLabel-root": {
                                color: "#000",
                            },
                            "& .MuiInputBase-input": {
                                color: "#000",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Last Name"
                        value={newUserLastName}
                        onChange={(e) => setNewUserLastName(e.target.value)}
                        style={{ marginTop: "16px" }}
                        sx={{
                            marginTop: "10px",
                            "& .MuiInputLabel-root": {
                                color: "#000",
                            },
                            "& .MuiInputBase-input": {
                                color: "#000",
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleAddUser} color="primary">
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog   PaperProps={{
                sx: {
                    backgroundColor: cardBgColor,
                    color: textColor, // so text inherits this color by default
                },
            }} open={isAddOrgDialogOpen} onClose={handleCloseAddOrgDialog}>
                <DialogTitle style={{ color: textColor }}>Add Organization</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Name"
                        variant="outlined"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        sx={{
                            marginTop: "10px",
                            "& .MuiInputLabel-root": {
                                color: textColor,
                            },
                            "& .MuiInputBase-input": {
                                color: textColor,
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Phone"
                        variant="outlined"
                        value={orgPhone}
                        onChange={(e) => setOrgPhone(e.target.value)}
                        sx={{
                            marginTop: "16px",
                            "& .MuiInputLabel-root": {
                                color: textColor,
                            },
                            "& .MuiInputBase-input": {
                                color: textColor,
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Website"
                        variant="outlined"
                        value={orgWebsite}
                        onChange={(e) => setOrgWebsite(e.target.value)}
                        sx={{
                            marginTop: "16px",
                            "& .MuiInputLabel-root": {
                                color: textColor,
                            },
                            "& .MuiInputBase-input": {
                                color: textColor,
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Address"
                        variant="outlined"
                        value={orgAddress}
                        onChange={(e) => setOrgAddress(e.target.value)}
                        sx={{
                            marginTop: "16px",
                            "& .MuiInputLabel-root": {
                                color: textColor,
                            },
                            "& .MuiInputBase-input": {
                                color: textColor,
                            },
                        }}
                    />

                    <Autocomplete
                        options={parentOptions}
                        value={parentObj}
                        loading={parentLoading}
                        onChange={(e, v) => {
                            setParentObj(v || null);
                            setOrgParentId(v?.id || "");
                        }}
                        inputValue={parentSearch}
                        onInputChange={(e, v) => setParentSearch(v)}
                        getOptionLabel={(opt) => opt?.name || ""}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Parent Organization"
                                variant="outlined"
                                sx={{
                                    marginTop: "16px",
                                    "& .MuiInputLabel-root": { color: textColor },
                                    "& .MuiInputBase-input": { color: textColor },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {parentLoading ? <CircularProgress size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddOrgDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleAddOrg} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/*

      {/**!/*/}
            <Dialog open={isDeleteFinalConfirmOpen} onClose={closeFinalDeleteDialog}>
                <DialogTitle color="primary">Are you sure?</DialogTitle>
                <DialogContent>
                    <MDTypography variant="button" color="primary">
                        Are you sure you want to delete{" "}
                        <strong>{orgToDelete?.name}</strong>?
                        <br />
                        This will permanently remove the organization.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFinalDeleteDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleFinalDelete} color="primary">
                        Yes, Permanently Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default Organizations;
